import { useRef, useState, type FormEvent, type ReactNode } from "react";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from "../molecules/index.js";
import { STATE_RING } from "../lib/state-ring.js";
import type {
  PlayerAIChatMessage,
  PlayerAIMessageFeedback,
  PlayerAIMessageFeedbackRequest,
} from "./playerai.types";

const MESSAGE_FEEDBACK_COMMENT_MAX_LENGTH = 500;

/**
 * The response action row: revealed with its response on hover or focus, and
 * always on a device that cannot hover. Shared by the feedback-bearing row and
 * the row a response without feedback still gets for Reply and Copy.
 */
export const RESPONSE_ACTIONS_ROW =
  "mt-0.5 flex items-center gap-0.5 transition-opacity group-hover/assistant:opacity-100 group-focus-within/assistant:opacity-100 [@media(hover:none)]:opacity-100";

export function MessageFeedbackControls({
  message,
  conversationId,
  onSubmit,
  leading,
}: {
  message: PlayerAIChatMessage;
  conversationId: string;
  onSubmit: (
    feedback: PlayerAIMessageFeedbackRequest,
  ) => Promise<PlayerAIMessageFeedback>;
  /** Actions that lead the row (Reply, Copy), in front of the thumbs. */
  leading?: ReactNode;
}) {
  const [feedbackOverride, setFeedbackOverride] = useState<
    PlayerAIMessageFeedback | undefined
  >(undefined);
  const [optimisticRating, setOptimisticRating] = useState<
    PlayerAIMessageFeedback["rating"] | null
  >(null);
  const [activeRating, setActiveRating] = useState<
    PlayerAIMessageFeedback["rating"] | null
  >(null);
  const [draft, setDraft] = useState(message.viewerFeedback?.comment ?? "");
  const [draftDirty, setDraftDirty] = useState(false);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const helpfulTriggerRef = useRef<HTMLButtonElement | null>(null);
  const unhelpfulTriggerRef = useRef<HTMLButtonElement | null>(null);
  const persistedFeedback = feedbackOverride ?? message.viewerFeedback ?? null;
  const selectedRating = optimisticRating ?? persistedFeedback?.rating ?? null;

  const saveRating = async (rating: PlayerAIMessageFeedback["rating"]) => {
    const reopening = !open;
    const comment = draftDirty
      ? draft.trim() || null
      : (persistedFeedback?.comment ?? null);
    const ratingUnchanged = selectedRating === rating;
    setActiveRating(rating);
    setOpen(true);
    if (reopening && !draftDirty) setDraft(persistedFeedback?.comment ?? "");
    if (ratingUnchanged) return;
    setOptimisticRating(rating);
    setError(null);
    setPending(true);
    try {
      const saved = await onSubmit({
        conversationId,
        messageId: message.id,
        rating,
        comment,
      });
      setFeedbackOverride(saved);
      setOptimisticRating(null);
    } catch {
      setError("Feedback could not be saved. Please try again.");
    } finally {
      setPending(false);
    }
  };

  const normalizedDraft = draft.trim() || null;
  const feedbackUnchanged =
    persistedFeedback !== null &&
    activeRating === persistedFeedback.rating &&
    normalizedDraft === persistedFeedback.comment;

  const saveComment = async (event: FormEvent) => {
    event.preventDefault();
    if (!activeRating) return;
    setError(null);
    setPending(true);
    try {
      const saved = await onSubmit({
        conversationId,
        messageId: message.id,
        rating: activeRating,
        comment: draft.trim() || null,
      });
      setFeedbackOverride(saved);
      setOptimisticRating(null);
      setDraft(saved.comment ?? "");
      setDraftDirty(false);
      setOpen(false);
      const triggerRef =
        activeRating === "helpful" ? helpfulTriggerRef : unhelpfulTriggerRef;
      window.setTimeout(() => triggerRef.current?.focus(), 0);
    } catch {
      setError("Feedback could not be saved. Please try again.");
    } finally {
      setPending(false);
    }
  };

  const feedbackForm = (
    <PopoverContent
      aria-label="Response feedback"
      padding="md"
      className="w-72"
      sideOffset={6}
    >
      <form onSubmit={(event) => void saveComment(event)}>
        <label
          htmlFor={`player-ai-feedback-${message.id}`}
          className="block text-xs font-medium text-pb-text"
        >
          Optional feedback
        </label>
        <textarea
          // eslint-disable-next-line jsx-a11y/no-autofocus -- the dialog opens from an explicit rating action and must receive focus before its trigger becomes disabled
          autoFocus
          id={`player-ai-feedback-${message.id}`}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setDraftDirty(true);
          }}
          maxLength={MESSAGE_FEEDBACK_COMMENT_MAX_LENGTH}
          rows={3}
          placeholder="What worked, or what could be better?"
          className="mt-1.5 w-full resize-none rounded-md border border-pb-control-border bg-pb-card px-2.5 py-2 text-xs text-pb-text placeholder:text-pb-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal"
        />
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="text-dense-2 text-pb-text-muted">
            {draft.length}/{MESSAGE_FEEDBACK_COMMENT_MAX_LENGTH}
          </span>
          <button
            type="submit"
            disabled={pending || feedbackUnchanged}
            className="h-7 rounded-md bg-pb-accent px-3 text-xs font-medium text-pb-accent-foreground transition-colors hover:bg-pb-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {pending ? "Saving…" : "Save feedback"}
          </button>
        </div>
        {error && (
          <p role="alert" className="mt-1.5 text-dense-1 text-pb-red-text">
            {error}
          </p>
        )}
      </form>
    </PopoverContent>
  );

  const renderThumb = (
    rating: PlayerAIMessageFeedback["rating"],
    label: string,
  ) => {
    const selected = selectedRating === rating;
    const Icon = rating === "helpful" ? ThumbsUp : ThumbsDown;
    return (
      <Popover
        key={rating}
        open={open && activeRating === rating}
        onOpenChange={setOpen}
        align="end"
        contentRole="dialog"
      >
        {/* The hint is the name, so it does not describe the button a
            second time; it stays quiet while the feedback form is open. */}
        <Tooltip
          content={label}
          describeChild={false}
          disabled={open && activeRating === rating}
        >
          <PopoverTrigger>
            <button
              type="button"
              ref={
                rating === "helpful" ? helpfulTriggerRef : unhelpfulTriggerRef
              }
              aria-label={label}
              aria-pressed={selected}
              disabled={pending}
              onClick={() => void saveRating(rating)}
              className={`inline-flex h-6 w-6 items-center justify-center rounded text-pb-text-muted transition-colors hover:pb-overlay-paint hover:text-pb-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal disabled:cursor-wait disabled:opacity-50 ${
                selected
                  ? `bg-pb-purple-soft text-pb-purple-text ${STATE_RING.purple}`
                  : ""
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
        </Tooltip>
        {activeRating === rating ? feedbackForm : null}
      </Popover>
    );
  };

  return (
    <div
      className={`${RESPONSE_ACTIONS_ROW} ${
        selectedRating || open ? "opacity-100" : "opacity-0"
      }`}
      role="group"
      aria-label="Response actions"
    >
      {leading}
      {renderThumb("helpful", "Mark response helpful")}
      {renderThumb("unhelpful", "Mark response unhelpful")}
    </div>
  );
}
